import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ClearIcon from '@material-ui/icons/Clear';
import DoneIcon from '@material-ui/icons/Done';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

const useStyles = makeStyles(() => ({
    trigger: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 14,
        borderRadius: 8,
        padding: '5px 10px',
        borderColor: '#d5d7e8',
        color: '#515f6d',
        backgroundColor: '#fff',
        whiteSpace: 'nowrap',
        '&:hover': {
            borderColor: '#43419e',
            backgroundColor: '#f5f6ff',
        },
    },
    triggerActive: {
        borderColor: '#43419e',
        color: '#43419e',
        backgroundColor: '#f5f6ff',
    },
    triggerCount: {
        marginLeft: 6,
        minWidth: 20,
        height: 20,
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '20px',
        padding: '0 6px',
        color: '#fff',
        backgroundColor: '#43419e',
    },
    dialogPaper: {
        borderRadius: 12,
        // Keeps the option list from resizing the dialog as the search narrows
        // the results.
        height: '70vh',
        maxHeight: 620,
    },
    title: {
        padding: '16px 20px 12px',
    },
    titleText: {
        fontWeight: 700,
        fontSize: 18,
        color: '#14112d',
    },
    subTitleText: {
        fontSize: 13,
        fontWeight: 600,
        color: '#8a92a6',
        marginTop: 2,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0 20px',
        overflow: 'hidden',
    },
    searchField: {
        marginTop: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
        },
    },
    selectedStrip: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 12,
        maxHeight: 84,
        overflowY: 'auto',
    },
    selectedChip: {
        maxWidth: 240,
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 12,
        color: '#43419e',
        borderColor: '#43419e',
        backgroundColor: '#f5f6ff',
    },
    bulkRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
    },
    countText: {
        fontSize: 13,
        fontWeight: 700,
        color: '#515f6d',
    },
    linkButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        color: '#43419e',
        padding: '2px 6px',
        minWidth: 'auto',
    },
    list: {
        flex: 1,
        marginTop: 8,
        overflowY: 'auto',
        border: '1px solid #ebedf8',
        borderRadius: 8,
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 8,
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#c7c6e2',
            borderRadius: 8,
        },
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 10px 2px 2px',
        cursor: 'pointer',
        borderBottom: '1px solid #f4f5fb',
        '&:hover': {
            backgroundColor: '#f5f6ff',
        },
    },
    optionSelected: {
        backgroundColor: '#f0f1ff',
    },
    optionLabel: {
        flex: 1,
        fontSize: 14,
        fontWeight: 600,
        color: '#14112d',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    optionLabelSelected: {
        color: '#43419e',
        fontWeight: 700,
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 120,
        fontSize: 14,
        fontWeight: 700,
        color: '#8a92a6',
    },
    actions: {
        padding: '12px 20px 16px',
        gap: 8,
    },
    applyButton: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 8,
        padding: '6px 20px',
        backgroundColor: '#43419e',
        color: '#fff',
        '&:hover': { backgroundColor: '#37357f' },
        '&.Mui-disabled': { backgroundColor: '#d5d7e8', color: '#fff' },
    },
    cancelButton: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 8,
        color: '#515f6d',
    },
}));

export interface FilterOption {
    label: string;
    value: any;
}

interface FilterSelectModalProps {
    /** Trigger label, e.g. "Vendor". */
    label: string;
    options?: FilterOption[];
    /** Committed selection. */
    value?: FilterOption[];
    /** Fires only on Apply / inline clear, never while browsing the list. */
    onChange: (selected: FilterOption[]) => void;
    searchPlaceholder?: string;
    disabled?: boolean;
    /** Shown on the trigger tooltip when nothing is selected. */
    helperText?: string;
}

const optionKey = (option: FilterOption) => String(option?.value);

/**
 * Multi-select filter rendered as a compact trigger plus a modal picker with
 * local search, select-all and an explicit Apply. Selections are staged inside
 * the modal so a half-finished pick never re-queries the list behind it.
 */
const FilterSelectModal: React.FC<FilterSelectModalProps> = ({
    label,
    options = [],
    value = [],
    onChange,
    searchPlaceholder,
    disabled = false,
    helperText,
}) => {
    const styles = useStyles();
    const [isOpen, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [draft, setDraft] = useState<FilterOption[]>([]);

    const selectedCount = value?.length ?? 0;
    const hasSelection = selectedCount > 0;

    // Re-seed the staged selection every time the modal opens so a cancelled
    // edit doesn't leak into the next one.
    useEffect(() => {
        if (isOpen) {
            setDraft(Array.isArray(value) ? value : []);
            setSearchText('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const draftKeys = useMemo(
        () => new Set((draft ?? []).map(optionKey)),
        [draft],
    );

    // Selected options float to the top so the current picks stay visible
    // without scrolling a long list.
    const visibleOptions = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        const matched = !term
            ? options
            : options.filter((option) =>
                  String(option?.label ?? '')
                      .toLowerCase()
                      .includes(term),
              );
        return [...matched].sort((a, b) => {
            const aSelected = draftKeys.has(optionKey(a)) ? 0 : 1;
            const bSelected = draftKeys.has(optionKey(b)) ? 0 : 1;
            if (aSelected !== bSelected) return aSelected - bSelected;
            return String(a?.label ?? '').localeCompare(String(b?.label ?? ''));
        });
        // `draftKeys` is intentionally excluded: re-sorting mid-click would make
        // rows jump out from under the cursor. The order is refreshed when the
        // search term changes or the modal reopens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, searchText, isOpen]);

    const toggleOption = (option: FilterOption) => {
        const key = optionKey(option);
        setDraft((prev) =>
            prev.some((item) => optionKey(item) === key)
                ? prev.filter((item) => optionKey(item) !== key)
                : [...prev, option],
        );
    };

    const selectAllVisible = () => {
        setDraft((prev) => {
            const keys = new Set(prev.map(optionKey));
            const additions = visibleOptions.filter(
                (option) => !keys.has(optionKey(option)),
            );
            return [...prev, ...additions];
        });
    };

    const isAllVisibleSelected =
        visibleOptions.length > 0 &&
        visibleOptions.every((option) => draftKeys.has(optionKey(option)));

    const handleApply = () => {
        onChange(draft);
        setOpen(false);
    };

    const handleInlineClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        onChange([]);
    };

    const triggerText = hasSelection
        ? selectedCount === 1
            ? (value[0]?.label ?? label)
            : label
        : label;

    const tooltipText = hasSelection
        ? value.map((item) => item?.label).join(', ')
        : (helperText ?? `Filter by ${label.toLowerCase()}`);

    return (
        <>
            <Tooltip title={tooltipText} arrow>
                <Box display="flex" alignItems="center">
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={disabled}
                        onClick={() => setOpen(true)}
                        className={`${styles.trigger} ${
                            hasSelection ? styles.triggerActive : ''
                        }`}
                        endIcon={
                            hasSelection ? undefined : <ArrowDropDownIcon />
                        }
                    >
                        <span
                            style={{
                                maxWidth: 130,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {triggerText}
                        </span>
                        {selectedCount > 1 && (
                            <span className={styles.triggerCount}>
                                {selectedCount}
                            </span>
                        )}
                        {hasSelection && (
                            <ClearIcon
                                fontSize="small"
                                onClick={handleInlineClear}
                                style={{ marginLeft: 4, fontSize: 16 }}
                            />
                        )}
                    </Button>
                </Box>
            </Tooltip>

            <Dialog
                open={isOpen}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
                classes={{ paper: styles.dialogPaper }}
            >
                <DialogTitle disableTypography className={styles.title}>
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography className={styles.titleText}>
                                {`Select ${label}`}
                            </Typography>
                            <Typography className={styles.subTitleText}>
                                {`${options.length} option${
                                    options.length === 1 ? '' : 's'
                                } available`}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent className={styles.content}>
                    <TextField
                        autoFocus
                        variant="outlined"
                        size="small"
                        className={styles.searchField}
                        placeholder={
                            searchPlaceholder ?? `Search ${label.toLowerCase()}`
                        }
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        fontSize="small"
                                        style={{ color: '#8a92a6' }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: !!searchText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchText('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {draft.length > 0 && (
                        <Box className={styles.selectedStrip}>
                            {draft.map((option) => (
                                <Chip
                                    key={optionKey(option)}
                                    size="small"
                                    variant="outlined"
                                    label={option?.label}
                                    className={styles.selectedChip}
                                    onDelete={() => toggleOption(option)}
                                />
                            ))}
                        </Box>
                    )}

                    <Box className={styles.bulkRow}>
                        <Typography className={styles.countText}>
                            {`${draft.length} selected`}
                        </Typography>
                        <Box>
                            <Button
                                className={styles.linkButton}
                                disabled={
                                    !visibleOptions.length ||
                                    isAllVisibleSelected
                                }
                                onClick={selectAllVisible}
                            >
                                {searchText.trim()
                                    ? 'Select matching'
                                    : 'Select all'}
                            </Button>
                            <Button
                                className={styles.linkButton}
                                disabled={!draft.length}
                                onClick={() => setDraft([])}
                            >
                                Clear all
                            </Button>
                        </Box>
                    </Box>

                    <Box className={styles.list}>
                        {visibleOptions.length ? (
                            visibleOptions.map((option) => {
                                const isSelected = draftKeys.has(
                                    optionKey(option),
                                );
                                return (
                                    <Box
                                        key={optionKey(option)}
                                        className={`${styles.option} ${
                                            isSelected
                                                ? styles.optionSelected
                                                : ''
                                        }`}
                                        onClick={() => toggleOption(option)}
                                    >
                                        {/* The whole row is the hit target, so
                                            the box only mirrors state. */}
                                        <Checkbox
                                            size="small"
                                            color="primary"
                                            checked={isSelected}
                                            tabIndex={-1}
                                            disableRipple
                                            inputProps={
                                                { readOnly: true } as any
                                            }
                                        />
                                        <Typography
                                            className={`${styles.optionLabel} ${
                                                isSelected
                                                    ? styles.optionLabelSelected
                                                    : ''
                                            }`}
                                            title={option?.label}
                                        >
                                            {option?.label}
                                        </Typography>
                                        {isSelected && (
                                            <DoneIcon
                                                fontSize="small"
                                                style={{ color: '#43419e' }}
                                            />
                                        )}
                                    </Box>
                                );
                            })
                        ) : (
                            <Box className={styles.emptyState}>
                                {options.length
                                    ? 'No matches for this search'
                                    : 'No options available'}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions className={styles.actions}>
                    <Button
                        className={styles.cancelButton}
                        onClick={() => setOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        className={styles.applyButton}
                        onClick={handleApply}
                    >
                        {draft.length
                            ? `Apply (${draft.length})`
                            : 'Apply'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FilterSelectModal;
